import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { HxButton } from '../button/button';
import { HxDialogRef } from '../dialog/dialog-ref';
import { HxDialogComponent } from '../dialog/dialog.component';
import { DIALOG_DATA, DIALOG_REF } from '../dialog/internal/dialog-injectors';
import { HxIconButton } from '../icon-button/icon-button';
import { HxSegmentComponent } from '../segmented-control/segment';
import { HxSegmentedControlComponent } from '../segmented-control/segmented-control';
import { type HxSegmentSelectionEvent } from '../segmented-control/types';
import { HxDropdownComponent } from '../select/dropdown/dropdown.component';
import {
  COLUMN_OPTIONS_EDITOR_OPERATORS,
  type ColumnOptionsEditorColumnDef,
  type ColumnOptionsEditorConfig,
  type OperatorMetadata,
  type RuleActionType,
  type RuleOperator,
  type UnifiedRule,
  validateColumnOptionsRules,
} from './column-options-editor.types';

@Component({
  selector: 'hx-column-options-editor',
  standalone: true,
  imports: [
    CommonModule,
    HxButton,
    HxDialogComponent,
    HxIconButton,
    HxSegmentedControlComponent,
    HxSegmentComponent,
    HxDropdownComponent,
  ],
  templateUrl: './column-options-editor.html',
  styleUrl: './column-options-editor.css',
  encapsulation: ViewEncapsulation.None,
})
export class HxColumnOptionsEditorDialogComponent implements OnInit {
  private readonly dialogRef =
    inject<HxDialogRef<HxColumnOptionsEditorDialogComponent, ColumnOptionsEditorConfig>>(DIALOG_REF);
  private readonly data = inject<ColumnOptionsEditorConfig>(DIALOG_DATA);

  readonly activeTab = signal<string>('');
  readonly columnsState = signal<ColumnOptionsEditorColumnDef[]>([]);
  readonly rulesState = signal<UnifiedRule[]>([]);
  readonly validationErrors = signal<string[]>([]);
  readonly showValidationErrors = signal<boolean>(false);

  readonly columnOptions = computed(() =>
    this.columnsState().map((col) => ({ id: col.key, value: col.key, label: col.label }))
  );

  readonly actionTypeOptions = computed(() => [
    { id: 'hide', value: 'hide' as RuleActionType, label: 'Hide value(s)' },
    { id: 'require', value: 'require' as RuleActionType, label: 'Require value(s)' },
    { id: 'disable', value: 'disable' as RuleActionType, label: 'Disable value(s)' },
  ]);

  ngOnInit(): void {
    // Initialize columnsState with deep-copied data from DIALOG_DATA
    const columnsToSet = this.data.columns.map((col) => {
      const mapped = {
        key: col.key,
        label: col.label,
        inputType: col.inputType,
        hint: col.hint,
        specialValue: col.specialValue,
        options: col.options.map((opt) => ({ ...opt })),
      };
      return mapped;
    });
    this.columnsState.set(columnsToSet);
    this.activeTab.set(this.data.columns[0]?.key ?? '');

    // Initialize rulesState
    const rulesToSet =
      this.data.rules.length > 0
        ? this.data.rules.map((r) => ({ ...r, when: { ...r.when }, then: { ...r.then } }))
        : (this.data.defaultRules ?? []).map((r) => ({ ...r, when: { ...r.when }, then: { ...r.then } }));
    this.rulesState.set(rulesToSet);
  }

  // ============================================================================
  // Tab
  // ============================================================================

  setTab(eventOrKey: HxSegmentSelectionEvent | string): void {
    if (typeof eventOrKey === 'string') {
      this.activeTab.set(eventOrKey);
    } else if (eventOrKey && typeof eventOrKey === 'object' && 'selectedKey' in eventOrKey) {
      this.activeTab.set(eventOrKey.selectedKey as string);
    }
  }

  // ============================================================================
  // Option CRUD (generic — operates on a column by key)
  // ============================================================================

  getColumnState(key: string): ColumnOptionsEditorColumnDef | undefined {
    return this.columnsState().find((col) => col.key === key);
  }

  addOption(key: string): void {
    this.columnsState.update((cols) =>
      cols.map((col) => {
        if (col.key !== key) return col;
        const label = `${col.label} Option ${col.options.length + 1}`;
        return {
          ...col,
          options: [
            ...col.options,
            {
              value: this.generateValue(
                label,
                col.options.map((o) => o.value)
              ),
              label,
            },
          ],
        };
      })
    );
  }

  removeOption(key: string, index: number): void {
    this.columnsState.update((cols) =>
      cols.map((col) => (col.key !== key ? col : { ...col, options: col.options.filter((_, i) => i !== index) }))
    );
  }

  updateOptionLabel(key: string, index: number, label: string): void {
    if (!label.trim()) return;
    this.columnsState.update((cols) =>
      cols.map((col) =>
        col.key !== key
          ? col
          : {
              ...col,
              options: col.options.map((opt, i) => (i === index ? { ...opt, label } : opt)),
            }
      )
    );
  }

  // ============================================================================
  // Rules
  // ============================================================================

  getOperatorOptions(column: string | null): { id: string; value: RuleOperator; label: string }[] {
    if (!column) return [];
    const colDef = this.columnsState().find((col) => col.key === column);
    const isSingleValue = colDef?.inputType === 'radio' || colDef?.inputType === 'single-select';
    return COLUMN_OPTIONS_EDITOR_OPERATORS.filter(
      (op: OperatorMetadata) => !isSingleValue || !op.supportsMultipleValues
    ).map((op: OperatorMetadata) => ({
      id: op.value,
      value: op.value,
      label: op.label,
    }));
  }

  getConditionValueOptions(rule: UnifiedRule): { id: string; value: string; label: string }[] {
    return this.getColumnOptions(rule.when.column);
  }

  getTargetColumnOptions(_actionType: RuleActionType | null): { id: string; value: string; label: string }[] {
    return this.columnOptions();
  }

  getActionValueOptions(rule: UnifiedRule): { id: string; value: string; label: string }[] {
    if (!rule.then.targetColumn) return [];
    return this.getColumnOptions(rule.then.targetColumn);
  }

  addRule(): void {
    const firstKey = this.columnsState()[0]?.key ?? '';
    const secondKey = this.columnsState()[1]?.key ?? firstKey;

    const newRule: UnifiedRule = {
      id: `rule-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      when: { column: firstKey, operator: 'equals', values: [] },
      then: { type: 'hide', targetColumn: secondKey, targetValues: [] },
      enabled: true,
    };
    this.rulesState.update((rules) => [...rules, newRule]);
  }

  removeRule(id: string): void {
    this.rulesState.update((rules) => rules.filter((r) => r.id !== id));
    this.showValidationErrors.set(false);
  }

  toggleRuleEnabled(id: string): void {
    this.rulesState.update((rules) => rules.map((r) => (r.id === id ? { ...r, enabled: !r.enabled } : r)));
  }

  updateRuleConditionColumn(id: string, column: string): void {
    this.rulesState.update((rules) =>
      rules.map((r) => {
        if (r.id !== id) return r;
        const firstOp = COLUMN_OPTIONS_EDITOR_OPERATORS[0]?.value ?? 'equals';
        return { ...r, when: { column, operator: firstOp, values: [] } };
      })
    );
  }

  updateRuleConditionOperator(id: string, operator: RuleOperator): void {
    this.rulesState.update((rules) => rules.map((r) => (r.id === id ? { ...r, when: { ...r.when, operator } } : r)));
  }

  updateRuleConditionValues(id: string, values: string[]): void {
    this.rulesState.update((rules) => rules.map((r) => (r.id === id ? { ...r, when: { ...r.when, values } } : r)));
  }

  updateRuleActionType(id: string, type: RuleActionType): void {
    this.rulesState.update((rules) =>
      rules.map((r) => {
        if (r.id !== id) return r;
        const targetColumn = this.columnsState()[0]?.key ?? '';
        return { ...r, then: { type, targetColumn, targetValues: [] } };
      })
    );
  }

  updateRuleActionTargetColumn(id: string, targetColumn: string): void {
    this.rulesState.update((rules) =>
      rules.map((r) => (r.id !== id ? r : { ...r, then: { ...r.then, targetColumn, targetValues: [] } }))
    );
  }

  updateRuleActionValues(id: string, targetValues: string[]): void {
    this.rulesState.update((rules) =>
      rules.map((r) => (r.id === id ? { ...r, then: { ...r.then, targetValues } } : r))
    );
  }

  dismissValidationErrors(): void {
    this.showValidationErrors.set(false);
  }

  // ============================================================================
  // Reset / Save / Cancel
  // ============================================================================

  resetToDefaults(): void {
    // Use defaultColumns if available, otherwise use the initial columns
    const columnsToRestore = this.data.defaultColumns ?? this.data.columns;
    this.columnsState.set(
      columnsToRestore.map((col) => ({
        key: col.key,
        label: col.label,
        inputType: col.inputType,
        hint: col.hint,
        specialValue: col.specialValue,
        options: col.options.map((opt) => ({ ...opt })),
      }))
    );
    this.rulesState.set(
      (this.data.defaultRules ?? []).map((r) => ({ ...r, when: { ...r.when }, then: { ...r.then } }))
    );
    this.showValidationErrors.set(false);
  }

  save(): void {
    const config = this.buildConfig();
    const validation = validateColumnOptionsRules(config.rules);

    if (!validation.valid) {
      this.validationErrors.set(validation.errors);
      this.showValidationErrors.set(true);
      return;
    }

    this.dialogRef.close(config);
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  // ============================================================================
  // Private helpers
  // ============================================================================

  private getColumnOptions(key: string): { id: string; value: string; label: string }[] {
    return (this.columnsState().find((col) => col.key === key)?.options ?? []).map((opt) => ({
      id: opt.value,
      value: opt.value,
      label: opt.label,
    }));
  }

  private generateValue(label: string, existingValues: string[]): string {
    const base =
      label
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '') || 'option';

    let value = base;
    let counter = 1;
    while (existingValues.includes(value)) {
      value = `${base}-${counter++}`;
    }
    return value;
  }

  private buildConfig(): ColumnOptionsEditorConfig {
    const config = {
      columns: this.columnsState().map((col) => ({
        key: col.key,
        label: col.label,
        inputType: col.inputType,
        hint: col.hint,
        specialValue: col.specialValue,
        options: col.options.map((opt) => ({ ...opt })),
      })),
      rules: this.rulesState().map((r) => ({ ...r, when: { ...r.when }, then: { ...r.then } })),
      defaultRules: this.data.defaultRules
        ? this.data.defaultRules.map((r) => ({ ...r, when: { ...r.when }, then: { ...r.then } }))
        : undefined,
    };
    return config;
  }
}
